// frontend/src/components/JTLSyncAdmin.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './JTLSyncAdmin.css';

const JTLSyncAdmin = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [newInterval, setNewInterval] = useState(60);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const API_URL = '/api/jtl-sync';
  const token = localStorage.getItem('token');

  // Status abrufen
  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Fehler beim Laden des Status:', error);
    }
  };

  // Service starten
  const startService = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage(response.data.message);
      await fetchStatus();
    } catch (error) {
      setMessage('Fehler beim Starten des Services');
    } finally {
      setLoading(false);
    }
  };

  // Service stoppen
  const stopService = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/stop`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage(response.data.message);
      await fetchStatus();
    } catch (error) {
      setMessage('Fehler beim Stoppen des Services');
    } finally {
      setLoading(false);
    }
  };

  // Intervall ändern
  const updateInterval = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.put(
        `${API_URL}/interval`,
        { seconds: parseInt(newInterval) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage(response.data.message);
      await fetchStatus();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Fehler beim Setzen des Intervalls');
    } finally {
      setLoading(false);
    }
  };

  // Alle synchronisieren
  const syncAll = async () => {
    setSyncLoading(true);
    setSyncResult(null);
    try {
      const response = await axios.post(`${API_URL}/sync-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSyncResult(response.data.summary);
      setMessage('Synchronisation erfolgreich durchgeführt');
    } catch (error) {
      setMessage('Fehler bei der Synchronisation');
    } finally {
      setSyncLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // Status alle 5 Sekunden aktualisieren
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  if (!status) {
    return (
      <div className="jtl-sync-admin">
        <div className="loading-spinner">Lade Status...</div>
      </div>
    );
  }

  return (
    <div className="jtl-sync-admin">
      <div className="admin-header">
        <h2>🔄 JTL Status-Synchronisation</h2>
        <div className={`status-badge ${status.isRunning ? 'running' : 'stopped'}`}>
          {status.isRunning ? '🟢 Aktiv' : '🔴 Gestoppt'}
        </div>
      </div>

      {message && (
        <div className="admin-message">
          {message}
        </div>
      )}

      <div className="admin-cards">
        {/* Service Control Card */}
        <div className="admin-card">
          <h3>Service Steuerung</h3>
          <div className="card-content">
            <p className="status-info">
              Status: <strong>{status.isRunning ? 'Läuft' : 'Gestoppt'}</strong>
            </p>
            <p className="status-info">
              Intervall: <strong>{status.intervalSeconds}s</strong>
            </p>
            <div className="button-group">
              <button 
                className="control-button start"
                onClick={startService}
                disabled={loading || status.isRunning}
              >
                ▶️ Starten
              </button>
              <button 
                className="control-button stop"
                onClick={stopService}
                disabled={loading || !status.isRunning}
              >
                ⏸️ Stoppen
              </button>
            </div>
          </div>
        </div>

        {/* Interval Settings Card */}
        <div className="admin-card">
          <h3>Intervall einstellen</h3>
          <div className="card-content">
            <form onSubmit={updateInterval} className="interval-form">
              <div className="form-group">
                <label>Sync-Intervall (Sekunden):</label>
                <input 
                  type="number" 
                  min="30" 
                  max="3600"
                  value={newInterval}
                  onChange={(e) => setNewInterval(e.target.value)}
                  disabled={loading}
                />
                <small>Minimum: 30 Sekunden</small>
              </div>
              <button 
                type="submit" 
                className="control-button update"
                disabled={loading}
              >
                💾 Intervall ändern
              </button>
            </form>
          </div>
        </div>

        {/* Manual Sync Card */}
        <div className="admin-card">
          <h3>Manuelle Synchronisation</h3>
          <div className="card-content">
            <p className="info-text">
              Synchronisiert alle offenen Warenannahmen sofort mit JTL Wawi.
            </p>
            <button 
              className="control-button sync"
              onClick={syncAll}
              disabled={syncLoading}
            >
              {syncLoading ? '⏳ Synchronisiere...' : '🔄 Jetzt synchronisieren'}
            </button>
            
            {syncResult && (
              <div className="sync-result">
                <h4>Ergebnis:</h4>
                <ul>
                  <li>Geprüft: <strong>{syncResult.totalChecked}</strong></li>
                  <li>Aktualisiert: <strong>{syncResult.updated}</strong></li>
                  <li>Fehler: <strong>{syncResult.errors}</strong></li>
                </ul>
                <small className="timestamp">
                  {new Date(syncResult.timestamp).toLocaleString('de-DE')}
                </small>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="info-box">
        <h4>ℹ️ Wie funktioniert die Synchronisation?</h4>
        <ul>
          <li>Der Service prüft automatisch alle offenen Warenannahmen mit JTL-Bestellnummer</li>
          <li><strong>JTL Status 30</strong> → Dashboard Status "In Einlagerung"</li>
          <li><strong>JTL Status 500</strong> → Dashboard Status "Eingelagert"</li>
          <li>Status "Eingegangen" muss manuell gesetzt werden</li>
          <li>Bereits eingelagerte Warenannahmen werden nicht mehr geprüft</li>
        </ul>
      </div>
    </div>
  );
};

export default JTLSyncAdmin;